import type React from 'react'
import { useMemo } from 'react'
import { Drawer, Form, message } from 'antd'
import { GraviButton, Horizontal, Vertical, Texto } from '@gravitate-js/excalibrr'
import type { SupplierReconciliationMetadataResponse } from '../../../../../api/useMetadata/types'
import { useSupplierReconciliation } from '../../../../../api/useSupplierReconciliation'
import { roundToPrecision } from './utils/formatters'
import { useInvoiceFormState } from './hooks/useInvoiceFormState'
import { useInvoiceValidation } from './hooks/useInvoiceValidation'
import { useInvoiceCalculations } from './hooks/useInvoiceCalculations'
import { InvoiceHeaderForm } from './components/InvoiceHeaderForm'
import { InvoiceProductsSection } from './components/InvoiceProductsSection'
import { InvoiceTaxesSection } from './components/InvoiceTaxesSection'
import { InvoiceTotalsSection } from './components/InvoiceTotalsSection'
import type { InvoiceFormValues } from '@/api/useSupplierReconciliation/types/forms'
import { getFixedZoneTime } from '@/utils/intl/dateTimeFormatters'
import { get } from 'lodash'

type InvoiceDetail = {
  type: string
  bol_number: string | null
  bol_date?: string | null
  product: {
    source_name: string
    internal_id: string
    internal_name: string
  }
  total: number
  rate: number | null
  amount: number
  uom: string
  gross_volume: number | null
  net_volume: number | null
}
interface CreateInvoiceModalProps {
  open: boolean
  onCancel: () => void
  metadata?: SupplierReconciliationMetadataResponse
  metadataLoading: boolean
  onSuccess?: () => void
  handleCreateInvoice?: (invoiceData: { body: unknown }) => Promise<void>
}

export const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({
  open,
  onCancel,
  metadata,
  metadataLoading,
  onSuccess,
  handleCreateInvoice,
}) => {
  const { useCreateManualInvoice } = useSupplierReconciliation()
  const createInvoiceMutation = useCreateManualInvoice()

  const {
    form,
    handleCancel,
    handleClose,
    handleFormValuesChange,
    transformMetadataToDropdownOptions,
    getInitialValues,
    handleSuccessfulSubmission,
  } = useInvoiceFormState({ onCancel, onSuccess })

  const { validateFormSubmission } = useInvoiceValidation()
  const { calculateTotals, handleBillingMethodChange, clearTaxProductReferences } = useInvoiceCalculations()

  const dropdownOptions = useMemo(() => transformMetadataToDropdownOptions(metadata), [metadata])

  const handleBillingMethodChangeWrapper = (billingMethod: string) => {
    handleBillingMethodChange(billingMethod, form)
    calculateTotals(form)
  }

  const handleCalculateTotals = () => {
    calculateTotals(form)
  }

  const handleProductRemoved = (productId: string) => {
    clearTaxProductReferences(productId, form)
  }

  const buildInvoiceData = (values: InvoiceFormValues) => {
    const billingMethod = values.billingBy || 'net'

    const getSupplierName = (id: string) => {
      const supplier = metadata?.suppliers?.find((s: { id: string; name: string }) => s.id === id)
      return supplier?.name || id
    }

    const getTerminalName = (id: string) => {
      const terminal = metadata?.terminals?.find((t: { id: string; name: string }) => t.id === id)
      return terminal?.name || id
    }

    const getTerminalTimezone = (id: string) => {
      const terminal = metadata?.terminals?.find((t: { id: string; name: string; timezone?: string }) => t.id === id)
      return terminal?.timezone
    }

    const getTerminalInfo = (terminalValue: string | string[]) => {
      // Handle single terminal selection
      if (typeof terminalValue === 'string') {
        return {
          source_name: getTerminalName(terminalValue),
          internal_id: terminalValue,
          internal_name: getTerminalName(terminalValue),
          timezone: getTerminalTimezone(terminalValue),
        }
      }

      // Handle multiple terminal selection - use first terminal as primary
      if (Array.isArray(terminalValue) && terminalValue.length > 0) {
        const primaryTerminal = terminalValue[0]
        const terminalNames = terminalValue.map((id) => getTerminalName(id)).join(', ')
        return {
          source_name: terminalNames,
          internal_id: primaryTerminal,
          internal_name: terminalNames,
          timezone: getTerminalTimezone(primaryTerminal),
        }
      }

      // Fallback for empty values
      return {
        source_name: '',
        internal_id: '',
        internal_name: '',
      }
    }

    const getProductName = (id: string) => {
      const allProducts = Object.values(metadata?.products || {}).flat()
      const product = allProducts.find((p: { id: string; name: string; code?: string }) => p.id === id)
      return product?.name || id
    }

    const details: InvoiceDetail[] = []

    if (values.products) {
      values.products.forEach(
        (product: {
          product?: string
          bolNumber?: string
          bolDateTime?: any
          total?: number
          unitPrice?: number
          grossVol?: number
          netVol?: number
        }) => {
          if (product.product) {
            details.push({
              type: 'supply',
              bol_number: product.bolNumber || null,
              bol_date: product.bolDateTime
                ? getFixedZoneTime(product.bolDateTime, getTerminalInfo(values.terminal).timezone).toISOString()
                : null,
              product: {
                source_name: getProductName(product.product),
                internal_id: product.product,
                internal_name: getProductName(product.product),
              },
              total: roundToPrecision(product.total || 0, 2),
              rate: product.unitPrice ? roundToPrecision(product.unitPrice, 6) : null,
              amount: roundToPrecision(billingMethod === 'gross' ? product.grossVol || 0 : product.netVol || 0, 3),
              uom: 'gallons',
              gross_volume: product.grossVol ? roundToPrecision(product.grossVol, 3) : null,
              net_volume: product.netVol ? roundToPrecision(product.netVol, 3) : null,
            })
          }
        }
      )
    }

    if (values.taxes) {
      values.taxes.forEach(
        (tax: {
          taxDescription?: string
          product?: string
          amount?: number
          total?: number
          perUnit?: number
          taxType?: string
          taxAuthority?: string
          nonDeferred?: boolean
          state?: string
        }) => {
          let bolNumber = null
          if (tax.product && values.products) {
            const associatedProduct = values.products.find(
              (p: { product?: string; bolNumber?: string }) => p.product === tax.product
            )
            if (associatedProduct && associatedProduct.bolNumber) {
              bolNumber = associatedProduct.bolNumber
            }
          }

          details.push({
            type: 'tax',
            bol_number: bolNumber,
            product: tax.product
              ? {
                  source_name: getProductName(tax.product),
                  internal_id: tax.product,
                  internal_name: getProductName(tax.product),
                }
              : null,
            tax_description: tax.taxDescription || null,
            tax_type: tax.taxType || null,
            tax_authority: tax.taxAuthority || null,
            tax_non_deferred: tax.nonDeferred || false,
            state: tax.state || null,
            total: tax.total ? roundToPrecision(tax.total, 2) : 0,
            rate: tax.perUnit ? roundToPrecision(tax.perUnit, 6) : null,
            amount: tax.amount ? roundToPrecision(tax.amount, 3) : null,
          })
        }
      )
    }

    const terminal = getTerminalInfo(values.terminal)
    const timezone = terminal.timezone
    const bolDate = values.products?.find((p) => p.bolDateTime)?.bolDateTime?.toISOString() || null

    return {
      invoice_number: values.invoiceNumber,
      source_name: 'manual_entry',
      due_date_utc: getFixedZoneTime(values.invoiceDue, timezone)?.toISOString() || null,
      invoice_date: getFixedZoneTime(values.invoiceDate, timezone)?.toISOString() || null,
      bol_date: getFixedZoneTime(bolDate, timezone)?.toISOString() || null,
      supplier: {
        source_name: getSupplierName(values.supplier),
        internal_id: values.supplier,
        internal_name: getSupplierName(values.supplier),
      },
      terminal: terminal,
      details,
      void: false,
    }
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      if (!validateFormSubmission(form)) {
        return
      }

      const invoiceData = buildInvoiceData(values)

      if (handleCreateInvoice) {
        await handleCreateInvoice({ body: invoiceData })
      } else {
        await createInvoiceMutation.mutateAsync({ body: invoiceData })
      }

      handleSuccessfulSubmission()
    } catch (error: unknown) {
      console.error('Failed to create invoice:', error)

      // Extract and display the specific error message from API response
      let errorMessage = 'Failed to create invoice. Please try again.'

      // Handle multiple possible error structures
      if (error && typeof error === 'object') {
        // Try different possible error structures
        const anyError = error as any

        // OpenAPI React Query error structure
        if (anyError.error?.detail) {
          errorMessage = anyError.error.detail
        }
        // Standard axios/fetch error structure
        else if (anyError.response?.data?.detail) {
          errorMessage = anyError.response.data.detail
        }
        // Direct detail property
        else if (anyError.detail) {
          errorMessage = anyError.detail
        }
        // Message property
        else if (anyError.message) {
          errorMessage = anyError.message
        }
        // For debugging - log the error structure
        console.log('Error structure:', JSON.stringify(error, null, 2))
      } else if (error instanceof Error) {
        errorMessage = error.message
      }

      message.error(errorMessage)
    }
  }

  return (
    <Drawer
      title={<Texto>New Invoice</Texto>}
      open={open}
      onClose={handleClose}
      placement='bottom'
      height='70vh'
      destroyOnClose={true}
      styles={{
        body: { padding: 0 },
        wrapper: { width: '100%', minWidth: '1440px' },
        footer: {
          padding: 0,
          background: 'transparent',
        },
      }}
      footer={
        <Horizontal
          gap={16}
          style={{
            padding: 6,
            width: '99%',
            height: '100%',
          }}
          justifyContent='flex-end'
          bg='elevated'
        >
          <GraviButton key='cancel' onClick={handleCancel}>
            Cancel
          </GraviButton>

          <GraviButton
            key='submit'
            type='primary'
            theme2
            onClick={handleSubmit}
            loading={createInvoiceMutation.isPending}
            disabled={createInvoiceMutation.isPending}
          >
            Create Invoice
          </GraviButton>
        </Horizontal>
      }
    >
      <Form
        form={form}
        layout='vertical'
        style={{ padding: 0 }}
        onValuesChange={() => {
          handleCalculateTotals()
          handleFormValuesChange()
        }}
        initialValues={getInitialValues()}
      >
        <Form.Item noStyle shouldUpdate>
          {() => {
            const products = form.getFieldValue('products') || []
            const taxes = form.getFieldValue('taxes') || []
            const productTotal = form.getFieldValue('productTotal') || 0
            const taxTotal = form.getFieldValue('taxTotal') || 0
            const grandTotal = form.getFieldValue('grandTotal') || 0

            return (
              <Horizontal gap={0}>
                <InvoiceHeaderForm
                  form={form}
                  dropdownOptions={dropdownOptions}
                  metadataLoading={metadataLoading}
                  onBillingMethodChange={handleBillingMethodChangeWrapper}
                />

                <Vertical
                  gap={0}
                  border='top'
                  flex='1 1 auto'
                  height='100%'
                  style={{ display: 'flex', flexDirection: 'column' }}
                >
                  <Vertical gap={0} flex='1 1 auto' scroll height='100%'>
                    <Horizontal border='bottom' bg='elevated' className='p-3' verticalCenter>
                      <Texto category='p2' weight='bold'>
                        Products ({products.length})
                      </Texto>
                    </Horizontal>

                    <Form.List name='products'>
                      {(fields, { add, remove }) => (
                        <InvoiceProductsSection
                          form={form}
                          fields={fields}
                          add={add}
                          remove={remove}
                          products={products}
                          dropdownOptions={dropdownOptions}
                          metadataLoading={metadataLoading}
                          onCalculateTotals={handleCalculateTotals}
                          onProductRemoved={handleProductRemoved}
                        />
                      )}
                    </Form.List>

                    <Horizontal border='bottom' bg='elevated' className='p-3' verticalCenter>
                      <Texto category='p2' weight='bold'>
                        Taxes ({taxes.length})
                      </Texto>
                    </Horizontal>

                    <Form.List name='taxes'>
                      {(fields, { add, remove }) => (
                        <InvoiceTaxesSection
                          form={form}
                          fields={fields}
                          add={add}
                          remove={remove}
                          dropdownOptions={dropdownOptions}
                          onCalculateTotals={handleCalculateTotals}
                        />
                      )}
                    </Form.List>
                  </Vertical>

                  <Vertical flex='1 1 auto' style={{ minHeight: '60px' }} />

                  <InvoiceTotalsSection productTotal={productTotal} taxTotal={taxTotal} grandTotal={grandTotal} />
                </Vertical>
              </Horizontal>
            )
          }}
        </Form.Item>
      </Form>
    </Drawer>
  )
}
